import { formatDate } from "@elektr/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Badge, Card, PageHeader, Spinner } from "@/components/ui/card";
import { Field, Input, Textarea } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";

const STATUS_KEYS = {
  pending: "claim.status_pending",
  approved: "claim.status_approved",
  rejected: "claim.status_rejected",
} as const;
const TONES = { pending: "warning", approved: "success", rejected: "danger" } as const;

/** "Uyim ro'yxatda yo'q": uy boshqa raqamga yozilgan bo'lsa, mijoz so'rov yuboradi, admin tekshirib biriktiradi. */
export function ClaimPage() {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [address, setAddress] = useState("");
  const [fullName, setFullName] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const claims = useQuery({
    queryKey: ["my-claims"],
    queryFn: async () =>
      unwrap(await api.GET("/api/claims/", { params: { query: { page_size: 50 } } })),
  });

  const send = useMutation({
    mutationFn: async () =>
      unwrap(await api.POST("/api/claims/", { body: { address, full_name: fullName, note } })),
    onSuccess: () => {
      toast.success(t("claim.sent"));
      setAddress("");
      setFullName("");
      setNote("");
      setError(null);
      void qc.invalidateQueries({ queryKey: ["my-claims"] });
    },
    onError: (e) => setError(errorMessage(e, t("common.error"))),
  });
  const cancel = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.DELETE("/api/claims/{id}/", { params: { path: { id } } });
      if (!res.response.ok) throw new Error("cancel");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["my-claims"] }),
    onError: (e) => toast.error(errorMessage(e, t("common.error"))),
  });

  return (
    <>
      <PageHeader title={t("my.nav_claim")} />
      <Card className="mb-6">
        <p className="mb-4 text-sm text-muted-foreground">{t("claim.customer_hint")}</p>
        <form
          className="flex flex-col gap-4"
          noValidate
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            setError(null);
            send.mutate();
          }}
        >
          <Field label={t("claim.address")}>
            {(id) => <Input id={id} value={address} onChange={(e) => setAddress(e.target.value)} />}
          </Field>
          <Field label={t("claim.full_name")}>
            {(id) => (
              <Input id={id} value={fullName} onChange={(e) => setFullName(e.target.value)} />
            )}
          </Field>
          <Field label={t("claim.note")}>
            {(id) => (
              <Textarea
                id={id}
                rows={3}
                maxLength={1000}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
          {error && (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          )}
          <Button type="submit" disabled={send.isPending || address.trim().length < 5}>
            {t("claim.send")}
          </Button>
        </form>
      </Card>

      <h2 className="mb-2 text-lg font-bold">{t("claim.history")}</h2>
      {claims.isLoading && <Spinner />}
      {claims.data?.results.length === 0 && (
        <p className="text-sm text-muted-foreground">{t("claim.empty")}</p>
      )}
      <div className="flex flex-col gap-2">
        {claims.data?.results.map((c) => (
          <Card key={c.id} className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="font-medium">{c.address}</div>
              <div className="text-xs text-muted-foreground">{formatDate(c.created_at)}</div>
              {c.admin_note && <div className="text-sm">{c.admin_note}</div>}
            </div>
            <div className="flex items-center gap-2">
              <Badge tone={TONES[c.status]}>{t(STATUS_KEYS[c.status])}</Badge>
              {c.status === "pending" && (
                <Button size="sm" variant="outline" onClick={() => cancel.mutate(c.id)}>
                  {t("claim.cancel")}
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}
